/* eslint-disable max-lines */
import _ from "lodash";
import React, { useContext, useState } from "react";
import AppContext from "../../../AppContext";
import {
  getDataForMatrixForProductNumberAndDate,
  getCurrentOrder,
  getProductSizesByLengthForNumber,
} from "../../../selectors";
import {
  FlexBox,
  IncrementDecrementInput,
  HorizontalScrollableBox,
  SpacingBox,
  H4,
  If,
} from "../../../Components";
import { productNr } from "../../../utils/products";
import * as orderMatrixUtils from "../../../utils/orderMatrix";
import useMatrixDataState from "./useMatrixDataState";
import OrderMatrixRow from "./OrderMatrixRow";
import { OrderMatrixHeader } from "./OrderMatrixHeader";
import OrderMatrixRowPrice from "./OrderMatrixRowPrice";
import { formatMoney } from "../../../utils/money";
import { isOrderEditable } from "../../../utils/order";
import CopyPaste from "./CopyPaste";
import { getTranslateByScope } from "../../../i18n";

const INCREMENT_DECREMENT_WIDTH = 50;

export const calculateTotal = (totalObject) =>
  _.values(totalObject).reduce(
    (total, current) => total + _.values(current).reduce((t, c) => c + t, 0),
    0,
  );

const getInitialDataForProduct = ({
  order,
  product,
  deliveryDate,
  context,
  id,
  sizesByLength,
}) => {
  const fetchedData = order.doc.products[id]
    ? order.doc.products[id].data
    : null;

  return (
    fetchedData ||
    getDataForMatrixForProductNumberAndDate(context)({
      number: productNr(product),
      deliveryPeriodId: deliveryDate,
      sizesByLength,
    })
  );
};

const IncrementDecrementRowContainer = ({ children }) => (
  <FlexBox
    style={{
      position: "relative",
      right: 0,
      backgroundColor: "white",
      zIndex: 1000,
      minWidth: INCREMENT_DECREMENT_WIDTH,
      alignItems: "end",
    }}
  >
    {children}
  </FlexBox>
);

const IncrementDecrementColumnContainer = ({ children }) => (
  <FlexBox
    style={{ minWidth: INCREMENT_DECREMENT_WIDTH, justifyContent: "center" }}
  >
    {children}
  </FlexBox>
);

export default function OrderProductMatrix(props) {
  const product = props.product;
  const deliveryDate = props.deliveryDate;
  const total = props.total;
  const colors = props.colors;
  const currency = props.currency;
  const id = props.id;
  const productSeason = props.productSeason;
  const translate = getTranslateByScope("app.orderMatrix");

  const [mask, setMask] = useState(null);
  const context = useContext(AppContext);
  const { actions } = context;
  const order = getCurrentOrder(context);

  const sizesByLength = getProductSizesByLengthForNumber(context)(
    product.number,
  );

  const initialData = getInitialDataForProduct({
    order,
    product,
    deliveryDate,
    context,
    sizesByLength,
    id,
  });

  const {
    data,
    decrementForColor,
    decrementForSize,
    incrementForColor,
    incrementForSize,
    valueForSizeAndColor,
    updateValueForColorAndSize,
  } = useMatrixDataState({
    colors,
    sizesByLength,
    initialData,
    onUpdate: actions.onAddOrUpdateOrderProduct,
    id,
    order,
  });

  const copyRow = (length, color, sizesForLength) => {
    let newMask = {};

    sizesForLength.forEach((s) => {
      const amount = valueForSizeAndColor({
        length: length,
        color: color,
        size: s,
      });
      newMask[s] = amount;
    });

    console.log(newMask);

    setMask(newMask);
  };

  const pasteRow = (length, color) => {
    for (const [size, amount] of Object.entries(mask)) {
      console.log(size, amount);

      updateValueForColorAndSize({
        value: amount,
        length,
        color,
        size,
      });
    }

    context.actions.setToast({
      title: translate("copyPaste.title"),
      text: translate("copyPaste.description"),
    });
  };

  if (!colors || !sizesByLength) return null;
  const generalTotal = total ? calculateTotal(total) : 0;
  const isReadonly = !isOrderEditable(order.doc);

  return (
    <>
      <HorizontalScrollableBox maxWidth="100%">
        {_.keys(sizesByLength).map((l) => {
          const sizes = sizesByLength[l];

          return (
            <React.Fragment key={l}>
              <OrderMatrixHeader length={l} sizes={sizes} />
              {colors.map((c) => (
                <FlexBox key={c.code + l} style={{ height: "50px" }}>
                  <CopyPaste
                    copyRow={() => copyRow(l, c.code, sizes)}
                    pasteRow={() => pasteRow(l, c.code)}
                    mask={mask}
                  />
                  <OrderMatrixRow
                    isReadonly={isReadonly}
                    product={product}
                    updateValueForColorAndSize={updateValueForColorAndSize}
                    color={c}
                    length={l}
                    sizes={sizes}
                    valueForSizeAndColor={valueForSizeAndColor}
                    deliveryDate={deliveryDate}
                    number={productNr(product)}
                    season={productSeason}
                  />

                  <IncrementDecrementRowContainer>
                    <SpacingBox marginRight="lg">
                      <If condition={!isReadonly}>
                        {() => (
                          <IncrementDecrementInput
                            onIncrement={() =>
                              incrementForColor({ length: l, color: c })
                            }
                            onDecrement={() =>
                              decrementForColor({ length: l, color: c })
                            }
                            value={orderMatrixUtils.entriesSumForColor({
                              color: c.code,
                              length: l,
                              sizes,
                              data,
                            })}
                          />
                        )}
                      </If>
                    </SpacingBox>

                    <OrderMatrixRowPrice
                      color={c}
                      length={l}
                      total={total}
                      currency={currency}
                    />
                  </IncrementDecrementRowContainer>
                </FlexBox>
              ))}
              <If condition={colors.length > 1 && !isReadonly}>
                {() => (
                  <FlexBox>
                    <IncrementDecrementColumnContainer />
                    <div style={{ minWidth: "60px" }}></div>
                    {sizes.map((s) => (
                      <IncrementDecrementColumnContainer key={s}>
                        <IncrementDecrementInput
                          onIncrement={() =>
                            incrementForSize({ length: l, size: s })
                          }
                          onDecrement={() =>
                            decrementForSize({ length: l, size: s })
                          }
                          value={orderMatrixUtils.entriesSumForSize({
                            size: s,
                            length: l,
                            colors,
                            data,
                          })}
                        />
                      </IncrementDecrementColumnContainer>
                    ))}
                  </FlexBox>
                )}
              </If>
            </React.Fragment>
          );
        })}
      </HorizontalScrollableBox>
      <FlexBox flex={1} justifyContent="flex-end">
        <H4>{formatMoney(generalTotal, currency)}</H4>
      </FlexBox>
    </>
  );
}
